import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { PrimeIcons } from 'primereact/api';
import { classNames } from 'primereact/utils';
import { FormField } from '../../FormField/FormField';
import { ValidationErrors } from '../../../hooks/useForm';
import { CANCELED_BY_OPTIONS, CancellationReason, Reservation } from '../../../types/reservation';
import { CancellationForm } from '../validators';
import { canceledByOptions, cancellationReasonOptions } from '../constants';

interface Props {
  visible: boolean;
  onHide: () => void;
  resource: Reservation | undefined;
  isGroupReservation: boolean;
  isCancelingGroupWithMultipleUsers: boolean;
  getCouponRefundText: () => string;
  cancellationForm: CancellationForm;
  setCancellationFormFields: (fields: Partial<CancellationForm>) => void;
  cancellationErrors: ValidationErrors<CancellationForm> | undefined;
  onConfirm: () => void;
  isCanceling: boolean;
}

/** Cancels the whole reservation, for every client in it. */
export const CancelReservationDialog = ({
  visible,
  onHide,
  resource,
  isGroupReservation,
  isCancelingGroupWithMultipleUsers,
  getCouponRefundText,
  cancellationForm,
  setCancellationFormFields,
  cancellationErrors,
  onConfirm,
  isCanceling,
}: Props) => (
  <Dialog
    draggable={false}
    resizable={false}
    visible={visible}
    onHide={onHide}
    header='Cancelar reserva'
    className='cancellation-dialog big-modal'
    footer={
      <div className='dialog-footer'>
        <Button label='Volver' icon='pi pi-times' onClick={onHide} className='p-button-text' disabled={isCanceling} />
        <Button
          label='Confirmar cancelación'
          icon='pi pi-check'
          onClick={onConfirm}
          className='p-button-danger'
          autoFocus
          loading={isCanceling}
        />
      </div>
    }
  >
    <div className='cancellation-info warning'>
      <i className={classNames(PrimeIcons.EXCLAMATION_TRIANGLE, 'warning-icon')} />
      {isGroupReservation ? (
        <>
          <p className='warning-title'>Esta acción cancelará la reserva para todos los usuarios del grupo.</p>
          <p>Se enviará un correo electrónico a cada participante notificándole de la cancelación.</p>
        </>
      ) : (
        <>
          <p className='warning-title'>Esta acción cancelará la reserva.</p>
          <p>Se enviará un correo electrónico al cliente notificándole de la cancelación.</p>
        </>
      )}
    </div>
    {!isGroupReservation && resource?.userCouponId && (
      <div className='cancellation-info'>
        <i className={classNames(PrimeIcons.INFO_CIRCLE, 'coupon-info-icon')} />
        <p>{getCouponRefundText()}</p>
      </div>
    )}
    <form className='cancellation-form'>
      <FormField
        labelTitle='¿Quién cancela?'
        error={cancellationErrors?.canceledBy}
        required={resource?.externalUsers?.length === 1}
      >
        {resource?.externalUsers?.length === 1 ? (
          <Dropdown
            value={cancellationForm.canceledBy}
            options={canceledByOptions}
            onChange={(e) => setCancellationFormFields({ canceledBy: e.value })}
            placeholder='Selecciona quién cancela'
            invalid={!!cancellationErrors?.canceledBy}
          />
        ) : (
          <div className='cancellation-info'>
            <p>Cuando una reserva con más de un asistente se cancela, la cancelación recae sobre la empresa.</p>
          </div>
        )}
      </FormField>

      <FormField
        labelTitle='Motivo de cancelación'
        error={cancellationErrors?.cancellationReason}
        required={cancellationForm.canceledBy !== CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN && !isCancelingGroupWithMultipleUsers}
      >
        {cancellationForm.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN ||
        (resource?.externalUsers?.length && resource?.externalUsers?.length > 1) ? (
          <InputTextarea
            value={cancellationForm.customCancellationReason}
            onChange={(e) => setCancellationFormFields({ customCancellationReason: e.target.value })}
            rows={2}
            autoResize
            placeholder='Escribe el motivo por el que se cancela'
          />
        ) : (
          <Dropdown
            value={cancellationForm.cancellationReason}
            options={cancellationReasonOptions}
            onChange={(e) => setCancellationFormFields({ cancellationReason: e.value })}
            placeholder='Selecciona el motivo'
            invalid={!!cancellationErrors?.cancellationReason}
          />
        )}
      </FormField>

      {cancellationForm.cancellationReason === CancellationReason.OTHER &&
        cancellationForm.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_USER && (
          <FormField labelTitle='Especifica el motivo' error={cancellationErrors?.customCancellationReason} required>
            <InputTextarea
              value={cancellationForm.customCancellationReason}
              onChange={(e) => setCancellationFormFields({ customCancellationReason: e.target.value })}
              rows={2}
              autoResize
              placeholder='Escribe el motivo por el que se cancela'
              invalid={!!cancellationErrors?.customCancellationReason}
            />
          </FormField>
        )}
    </form>
  </Dialog>
);
